import { useState, useEffect, useRef } from 'react';

interface AccordionItem {
    id: string;
    title: string;
    content: React.ReactNode;
}

interface AccordionProps {
    items: AccordionItem[];
    oneOpenAtATime?: boolean;
    className?: string;
    allOpenByDefault?: boolean;
}

export const Accordion: React.FC<AccordionProps> = ({
    items,
    oneOpenAtATime = true,
    className = '',
    allOpenByDefault = false
}) => {
    const [openItems, setOpenItems] = useState<Set<string>>(() => {
        if (allOpenByDefault) {
            return new Set(items.map(item => item.id));
        }

        return new Set();
    });

    const accordionRef = useRef<HTMLDivElement>(null);

    const toggleItem = (itemId: string) => {
        setOpenItems(prev => {
            const newOpenItems = new Set(prev);
            
            if (oneOpenAtATime) {
                // Close all other items if oneOpenAtATime is true
                newOpenItems.clear();
                if (!prev.has(itemId)) {
                    newOpenItems.add(itemId);
                }
            } else {
                // Toggle the specific item
                if (prev.has(itemId)) {
                    newOpenItems.delete(itemId);
                } else {
                    newOpenItems.add(itemId);
                }
            }
            
            return newOpenItems;
        });
    };

    return (
        <div 
            ref={accordionRef}
            className={`accordion-container ${className}`}
            role="presentation"
        >
            <ul role="presentation" className="accordion-list">
                {items.map((item) => {
                    const isOpen = openItems.has(item.id);
                    
                    return (
                        <li 
                            key={item.id} 
                            role="presentation" 
                            className={`accordion-list-item ${isOpen ? 'active' : ''}`}
                        >
                            <h3 className="accordion-item" id={`accordion-heading-${item.id}`}>
                                <button
                                    className="accordion-trigger"
                                    type="button"
                                    id={`accordion-trigger-${item.id}`}
                                    aria-controls={`accordion-content-${item.id}`}
                                    aria-expanded={isOpen}
                                    onClick={() => toggleItem(item.id)}
                                >
                                    <span className="h6-style">{item.title}</span>
                                </button>
                            </h3>
                            <div
                                className="accordion-panel"
                                role="region"
                                id={`accordion-content-${item.id}`}
                                aria-hidden={!isOpen}
                                aria-labelledby={`accordion-heading-${item.id}`}
                            >
                                <div className="accordion-panel-content">
                                    {item.content}
                                </div>
                            </div>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
};
